import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE from '../utils/apiBase';

const API = API_BASE;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const finalizeSession = (data) => {
    const { token: newToken, user: userData } = data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data?.mfa_required || response.data?.mfa_enrollment_required) {
      // Password verified but a full privileged session is withheld until MFA passes.
      return response.data;
    }
    return finalizeSession(response.data);
  };

  const platformLogin = async (email, password) => {
    const response = await axios.post(`${API}/auth/platform-login`, { email, password });
    if (response.data?.mfa_required || response.data?.mfa_enrollment_required) {
      return response.data;
    }
    return finalizeSession(response.data);
  };

  // Second factor for login: exchange the short-lived pre-auth token + code for a full session.
  const verifyMfa = async (mfaToken, code) => {
    const response = await axios.post(`${API}/auth/mfa/verify`, { mfa_token: mfaToken, code });
    return finalizeSession(response.data);
  };

  // Mandatory-enrolment path (used when a role is under a mandatory MFA policy).
  const beginMfaEnrollment = async (mfaToken) => {
    const response = await axios.post(`${API}/auth/mfa/enroll/begin`, { mfa_token: mfaToken });
    return response.data;
  };

  const completeMfaEnrollment = async (mfaToken, code) => {
    const response = await axios.post(`${API}/auth/mfa/enroll/complete`, { mfa_token: mfaToken, code });
    const userData = finalizeSession(response.data);
    return { user: userData, recoveryCodes: response.data.recovery_codes || [] };
  };

  const register = async (email, password, name, role = 'employee') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/portal/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, {}, {
      headers: { 'X-Session-ID': sessionId }
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const isPlatformOwner = () => user?.role === 'platform_owner';

  const isAdmin = () => {
    return user?.role === 'super_admin' || user?.role === 'admin';
  };

  const isManagerOrAdmin = () => {
    return user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'branch_manager' || user?.role === 'manager';
  };

  const isAuditor = () => {
    return user?.role === 'auditor';
  };

  const isSuperAdmin = () => user?.role === 'super_admin';

  const getUserDepartment = () => (user?.department || '').trim() || null;

  const hasFullPortalAccess = () => {
    if (!user) return false;
    return user.role === 'super_admin' || user.role === 'admin' || (!isPlatformOwner() && !getUserDepartment());
  };

  const authHelpers = () => ({
    isAdmin: isAdmin(),
    isManagerOrAdmin: isManagerOrAdmin(),
    isAuditor: isAuditor(),
    isPlatformOwner: isPlatformOwner(),
  });

  const value = {
    user,
    token,
    loading,
    login,
    platformLogin,
    verifyMfa,
    beginMfaEnrollment,
    completeMfaEnrollment,
    register,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    isAdmin,
    isManagerOrAdmin,
    isAuditor,
    isSuperAdmin,
    isPlatformOwner,
    getUserDepartment,
    hasFullPortalAccess,
    authHelpers,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

